/**
 * Pre-task survey for the Information Sharing study.
 * Collects: demographics, political indicators, news consumption, sharing habits.
 * Does NOT ask about coding/work division (that's the phishing experiment).
 */

import React, { useState } from 'react';

interface InfoSharingPreSurveyProps {
  onComplete: (data: any) => void;
}

const S = {
  container: { maxWidth: '650px', margin: '20px auto', padding: '1.5rem', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' } as React.CSSProperties,
  section: { marginBottom: '2rem' } as React.CSSProperties,
  q: { fontSize: '0.95rem', marginBottom: '0.5rem', fontWeight: 600 } as React.CSSProperties,
  select: { width: '100%', padding: '10px 12px', fontSize: '0.95rem', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff' } as React.CSSProperties,
  row: { marginBottom: '1.25rem' } as React.CSSProperties,
};

// Module-level so its function identity is STABLE across parent re-renders.
// (Defining it inside the component made React remount every dropdown on each
// keystroke/selection — the "page reloads when I pick an option" flicker.)
const SelectField: React.FC<{
  label: string; field: string; form: any; set: (f: string, v: string) => void;
  options: { value: string; label: string }[]; required?: boolean;
}> = ({ label, field, form, set, options, required = true }) => (
  <div style={S.row}>
    <div style={S.q}>{label} {required && <span style={{ color: '#c53030' }}>*</span>}</div>
    <select value={form[field]} onChange={e => set(field, e.target.value)} style={S.select}>
      <option value="">Select...</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const InfoSharingPreSurvey: React.FC<InfoSharingPreSurveyProps> = ({ onComplete }) => {
  const [page, setPage] = useState(0);
  const [form, setForm] = useState({
    // Demographics
    age: '',
    gender: '',
    education: '',
    income: '',
    // Race + Hispanic origin captured as TWO items (US Census model): race
    // (White/Black/Asian/...) and a separate Hispanic/Latino yes-no, so combined
    // categories (e.g. White-Hispanic, Black-Hispanic, non-Hispanic White) are derivable.
    race: '',
    hispanic: '',
    // Political indicators
    partisanship: '',
    partisanshipStrength: '',
    // News consumption
    hoursReadingNews: '',
    preferredNewsMedium: '',
    // Sharing habits (NEW — addresses Reviewer 2)
    sharingFrequency: '',
    sharingType: '',
    sharingAudience: '',
    evaluationTime: '',
    // Prolific ID — captured BOTH ways (Prolific's recommendation): the URL
    // parameter auto-fills `prolificId` (editable confirm field), and the raw URL
    // value is also stored verbatim in `urlProlificId` so a typo in the field can
    // be detected by comparing the two.
    prolificId: '',
    urlProlificId: '',
    // CMB ideal-marker (attitude toward the color blue) — measured once, neutral filler.
    mb1: '', mb2: '', mb3: '',
  });

  // Capture Prolific ID from URL on mount → pre-fill the editable field + keep the
  // raw URL value separately.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get('PROLIFIC_PID') || params.get('prolific_pid') || '';
    if (pid) {
      setForm(prev => ({ ...prev, prolificId: prev.prolificId || pid, urlProlificId: pid }));
      localStorage.setItem('prolificId', pid);
    }
  }, []);

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  // Party noun for the dynamic strength question ("How strongly do you identify as ___?").
  const partyNoun = (p: string): string => ({
    democrat: 'a Democrat', republican: 'a Republican', independent: 'an Independent',
    libertarian: 'a Libertarian', green: 'a member of the Green Party',
  } as Record<string, string>)[p] || 'with your political views';
  const STRENGTH_LABELS: Record<string, string> = {
    '1': 'Not at all strongly', '2': 'Slightly', '3': 'Somewhat', '4': 'Moderately',
    '5': 'Fairly strongly', '6': 'Strongly', '7': 'Very strongly',
  };

  // Page 0: Demographics (Prolific ID required for payment matching)
  const page0Valid = form.prolificId.trim() && form.age && form.gender && form.education && form.race && form.hispanic;
  // Page 1: Political + News
  const page1Valid = form.partisanship && form.hoursReadingNews && form.preferredNewsMedium;
  // Page 2: Sharing habits
  const page2Valid = form.sharingFrequency && form.sharingType && form.sharingAudience && form.evaluationTime && form.mb1 && form.mb2 && form.mb3;
  // CMB ideal-marker items — politically-neutral object (attitude toward chocolate),
  // 7-pt agree scale. Switched from "the color blue" because blue is the U.S. Democratic
  // party color and would correlate with partisanship — a FOCAL construct here — which
  // violates the marker's required orthogonality. Storage key/form ids kept as
  // markerBlue / mb1-3 for schema stability (no Mongoose subschema rename needed).
  const MARKER_ITEMS = [
    { id: 'mb1', text: 'Chocolate is delicious.' },
    { id: 'mb2', text: 'I like eating chocolate.' },
    { id: 'mb3', text: 'Chocolate is a pleasant treat.' },
  ];

  const handleSubmit = () => {
    onComplete({
      ...form,
      // Flag this as an info-sharing participant for the backend.
      // NOTE: no coder/non-coder stratification fields — Study 2 has no cohort
      // assignment. The backend defaults codingPercentage/yearsOfCoding to 0 when
      // absent, so /initialize does not need them.
      experimentType: 'info-sharing',
      organizationId: 'prolific',
      organizationName: 'Prolific Panel',
      // CMB marker (attitude toward chocolate; key kept as markerBlue for schema
      // stability) — assembled into one object, stored Mixed.
      markerBlue: { b1: Number(form.mb1), b2: Number(form.mb2), b3: Number(form.mb3) },
    });
  };

  return (
    <div style={S.container}>
      <h2 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Background Survey</h2>
      <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1.5rem' }}>
        Page {page + 1} of 3
      </p>

      {page === 0 && (
        <section style={S.section}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#444' }}>About You</h3>

          {/* Prolific ID — pre-filled from the URL when present; editable as a
              fallback so the ID is always captured for payment matching. */}
          <div style={S.row}>
            <div style={S.q}>Your Prolific ID <span style={{ color: '#c53030' }}>*</span></div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '6px' }}>
              {form.urlProlificId
                ? 'We detected your Prolific ID automatically — please confirm it is correct.'
                : 'Please paste your Prolific ID (from your Prolific account) so we can approve your submission.'}
            </div>
            <input
              type="text"
              value={form.prolificId}
              onChange={e => set('prolificId', e.target.value.trim())}
              placeholder="e.g. 5f1a2b3c4d5e6f7a8b9c0d1e"
              style={S.select}
            />
          </div>

          <SelectField form={form} set={set} label="Age" field="age" options={[
            { value: '18-24', label: '18-24' },
            { value: '25-34', label: '25-34' },
            { value: '35-44', label: '35-44' },
            { value: '45-54', label: '45-54' },
            { value: '55-64', label: '55-64' },
            { value: '65+', label: '65 or older' },
          ]} />

          <SelectField form={form} set={set} label="Gender" field="gender" options={[
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'non-binary', label: 'Non-binary' },
            { value: 'prefer-not-to-say', label: 'Prefer not to say' },
          ]} />

          <SelectField form={form} set={set} label="Highest level of education" field="education" options={[
            { value: 'high-school', label: 'High school or equivalent' },
            { value: 'some-college', label: 'Some college' },
            { value: 'associates', label: 'Associate\'s degree' },
            { value: 'bachelors', label: 'Bachelor\'s degree' },
            { value: 'masters', label: 'Master\'s degree' },
            { value: 'doctorate', label: 'Doctorate or professional degree' },
          ]} />

          <SelectField form={form} set={set} label="Annual household income" field="income" required={false} options={[
            { value: 'under-25k', label: 'Under $25,000' },
            { value: '25k-50k', label: '$25,000 - $49,999' },
            { value: '50k-75k', label: '$50,000 - $74,999' },
            { value: '75k-100k', label: '$75,000 - $99,999' },
            { value: '100k-150k', label: '$100,000 - $149,999' },
            { value: '150k+', label: '$150,000 or more' },
            { value: 'prefer-not', label: 'Prefer not to say' },
          ]} />

          {/* Race — asked separately from Hispanic/Latino origin (US Census model) */}
          <SelectField form={form} set={set} label="Which best describes your race?" field="race" options={[
            { value: 'white', label: 'White' },
            { value: 'black', label: 'Black or African American' },
            { value: 'asian', label: 'Asian' },
            { value: 'american-indian', label: 'American Indian or Alaska Native' },
            { value: 'pacific-islander', label: 'Native Hawaiian or Other Pacific Islander' },
            { value: 'middle-eastern', label: 'Middle Eastern or North African' },
            { value: 'multiracial', label: 'Two or more races' },
            { value: 'other', label: 'Other' },
            { value: 'prefer-not', label: 'Prefer not to say' },
          ]} />

          <SelectField form={form} set={set} label="Are you of Hispanic, Latino, or Spanish origin?" field="hispanic" options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
            { value: 'prefer-not', label: 'Prefer not to say' },
          ]} />

          <div style={{ textAlign: 'right', marginTop: '1.5rem' }}>
            <button onClick={() => setPage(1)} disabled={!page0Valid}
              style={{ padding: '10px 32px', backgroundColor: page0Valid ? '#0078d4' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: page0Valid ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
              Next
            </button>
          </div>
        </section>
      )}

      {page === 1 && (
        <section style={S.section}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#444' }}>Political Views & News Consumption</h3>

          <SelectField form={form} set={set} label="Which best describes your political affiliation?" field="partisanship" options={[
            { value: 'democrat', label: 'Democrat' },
            { value: 'republican', label: 'Republican' },
            { value: 'independent', label: 'Independent' },
            { value: 'libertarian', label: 'Libertarian' },
            { value: 'green', label: 'Green' },
            { value: 'no-preference', label: 'No preference' },
          ]} />

          {/* Strength as a slider, asked only once a party is chosen and worded
              with that party ("How strongly do you identify as a Democrat?"). */}
          {form.partisanship && form.partisanship !== 'no-preference' && (
            <div style={S.row}>
              <div style={S.q}>How strongly do you identify as {partyNoun(form.partisanship)}?</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 2px' }}>
                <span style={{ fontSize: '0.72rem', color: '#888', textAlign: 'center', width: '60px', lineHeight: 1.2 }}>Not at all<br/>strongly</span>
                <input
                  type="range" min={1} max={7} step={1}
                  value={form.partisanshipStrength || '4'}
                  onChange={e => set('partisanshipStrength', e.target.value)}
                  style={{ flex: 1, accentColor: '#0078d4', cursor: 'pointer', height: '6px' }}
                  aria-label="Strength of political identification"
                />
                <span style={{ fontSize: '0.72rem', color: '#888', textAlign: 'center', width: '60px', lineHeight: 1.2 }}>Very<br/>strongly</span>
              </div>
              <div style={{ textAlign: 'center', fontSize: '0.85rem', color: form.partisanshipStrength ? '#0078d4' : '#aaa', fontWeight: 600, marginTop: '4px' }}>
                {form.partisanshipStrength ? STRENGTH_LABELS[form.partisanshipStrength] : 'Drag the slider to answer'}
              </div>
            </div>
          )}

          <SelectField form={form} set={set} label="How many hours do you spend reading news online per day?" field="hoursReadingNews" options={[
            { value: 'less-than-1', label: 'Less than 1 hour' },
            { value: '1-3', label: '1-3 hours' },
            { value: '3-5', label: '3-5 hours' },
            { value: 'more-than-5', label: 'More than 5 hours' },
            { value: 'dont-read', label: 'I don\'t read news online' },
          ]} />

          <SelectField form={form} set={set} label="What is your preferred news source?" field="preferredNewsMedium" options={[
            { value: 'newspaper', label: 'Newspaper (print or online)' },
            { value: 'television', label: 'Television' },
            { value: 'online-news', label: 'Online news websites' },
            { value: 'social-media', label: 'Social media feeds' },
            { value: 'podcasts', label: 'Podcasts' },
            { value: 'other', label: 'Other' },
          ]} />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <button onClick={() => setPage(0)} style={{ padding: '10px 24px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff' }}>Back</button>
            <button onClick={() => setPage(2)} disabled={!page1Valid}
              style={{ padding: '10px 32px', backgroundColor: page1Valid ? '#0078d4' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: page1Valid ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
              Next
            </button>
          </div>
        </section>
      )}

      {page === 2 && (
        <section style={S.section}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#444' }}>Your Sharing Habits</h3>

          <SelectField form={form} set={set} label="How often do you share news articles on social media?" field="sharingFrequency" options={[
            { value: 'never', label: 'Never' },
            { value: 'rarely', label: 'Rarely (a few times a year)' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'daily', label: 'Daily or almost daily' },
          ]} />

          <SelectField form={form} set={set} label="When you share news, do you typically post publicly or forward privately?" field="sharingType" options={[
            { value: 'always-public', label: 'Always post publicly' },
            { value: 'mostly-public', label: 'Mostly post publicly' },
            { value: 'about-equal', label: 'About equal' },
            { value: 'mostly-private', label: 'Mostly forward privately' },
            { value: 'always-private', label: 'Always forward privately' },
          ]} />

          <SelectField form={form} set={set} label="Who do you typically share news with?" field="sharingAudience" options={[
            { value: 'general-public', label: 'General public / followers' },
            { value: 'friends', label: 'Friends only' },
            { value: 'family', label: 'Family' },
            { value: 'colleagues', label: 'Colleagues' },
            { value: 'mixed', label: 'A mix of the above' },
          ]} />

          <SelectField form={form} set={set} label="How much time do you usually spend reading an article before deciding to share?" field="evaluationTime" options={[
            { value: 'headline-only', label: 'I usually just read the headline' },
            { value: 'skim-first-paragraph', label: 'I skim the first paragraph' },
            { value: 'read-most', label: 'I read most of the article' },
            { value: 'read-entire', label: 'I read the entire article' },
            { value: 'read-and-verify', label: 'I read it and check other sources' },
          ]} />

          {/* CMB ideal-marker: attitude toward the color blue — neutral filler, 7-pt agree. */}
          <div style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
            <div style={S.q}>Finally, a few general preference questions. Please rate your agreement: <span style={{ color: '#c53030' }}>*</span></div>
            {MARKER_ITEMS.map(item => (
              <div key={item.id} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.9rem', color: '#444', marginBottom: '4px' }}>{item.text}</div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.7rem', color: '#999', width: '54px' }}>Strongly disagree</span>
                  {[1,2,3,4,5,6,7].map(v => {
                    const active = (form as any)[item.id] === String(v);
                    return (
                      <label key={v} onClick={() => set(item.id, String(v))}
                        style={{ flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem',
                          border: active ? '2px solid #0078d4' : '1px solid #dee2e6', background: active ? '#e3f2fd' : '#fff', fontWeight: active ? 600 : 400 }}>
                        {v}
                      </label>
                    );
                  })}
                  <span style={{ fontSize: '0.7rem', color: '#999', width: '54px' }}>Strongly agree</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <button onClick={() => setPage(1)} style={{ padding: '10px 24px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff' }}>Back</button>
            <button onClick={handleSubmit} disabled={!page2Valid}
              style={{ padding: '10px 32px', backgroundColor: page2Valid ? '#28a745' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: page2Valid ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '1rem' }}>
              Start Study
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default InfoSharingPreSurvey;
